'use client';

import { TechnologyOption } from '@/lib/tech-stack-types';
import { getCategoryDisplayName } from '@/lib/visualization-data';
import { useState } from 'react';

export interface VisualizationFilters {
  categories: string[];
  maturityLevels: string[];
  licenseTypes: string[];
  complianceRequirements: string[];
  popularityTrends: string[];
  communitySizes: string[];
  priceRanges: string[];
}

interface TechStackVisualizationFiltersProps {
  technologies: TechnologyOption[];
  filters: VisualizationFilters;
  onFiltersChange: (filters: VisualizationFilters) => void;
}

export default function TechStackVisualizationFilters({
  technologies,
  filters,
  onFiltersChange,
}: TechStackVisualizationFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract unique values from technologies
  const categories = [...new Set(technologies.map(t => t.category))];
  const maturityLevels = ['experimental', 'early', 'stable', 'mature', 'legacy'];
  const licenseTypes = ['open-source', 'commercial', 'freemium', 'enterprise-only'];
  const complianceOptions = [
    { key: 'gdpr', label: 'GDPR' },
    { key: 'hipaa', label: 'HIPAA' },
    { key: 'soc2', label: 'SOC 2' },
    { key: 'pci', label: 'PCI DSS' },
  ];
  const popularityTrends = ['declining', 'stable', 'growing', 'rapidly-growing'];
  const communitySizes = ['small', 'medium', 'large', 'very-large'];
  const priceRanges = ['free', 'freemium', 'usage-based', 'per-seat', 'enterprise'];

  const toggleFilter = <K extends keyof VisualizationFilters>(
    filterKey: K,
    value: string
  ) => {
    const currentValues = filters[filterKey];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];

    onFiltersChange({
      ...filters,
      [filterKey]: newValues,
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      categories: [],
      maturityLevels: [],
      licenseTypes: [],
      complianceRequirements: [],
      popularityTrends: [],
      communitySizes: [],
      priceRanges: [],
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).reduce((sum, arr) => sum + arr.length, 0);
  };

  const activeCount = getActiveFilterCount();

  return (
    <div className="bg-surface rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-background/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg font-semibold text-foreground">Filters</span>
          {activeCount > 0 && (
            <span className="bg-accent text-accent-foreground text-xs font-bold px-2 py-1 rounded">
              {activeCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {activeCount > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearAllFilters();
              }}
              className="text-sm text-foreground-secondary hover:text-foreground underline"
            >
              Clear all
            </button>
          )}
          <span className="text-foreground-secondary text-xl">{isExpanded ? '−' : '+'}</span>
        </div>
      </button>

      {/* Filters Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-6">
          {/* Category */}
          <div>
            <label className="text-sm font-semibold text-foreground-secondary mb-2 block">
              Category
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => toggleFilter('categories', category)}
                  className={`px-3 py-1.5 rounded text-sm transition-colors ${
                    filters.categories.includes(category)
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-background text-foreground-secondary hover:bg-background/80 border border-border'
                  }`}
                >
                  {getCategoryDisplayName(category)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Maturity Level */}
            <div>
              <label className="text-sm font-semibold text-foreground-secondary mb-2 block">
                Maturity Level
              </label>
              <div className="space-y-2">
                {maturityLevels.map(level => (
                  <label key={level} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.maturityLevels.includes(level)}
                      onChange={() => toggleFilter('maturityLevels', level)}
                      className="w-4 h-4 rounded border-border bg-background text-accent focus:ring-ring focus:ring-offset-0"
                    />
                    <span className="text-sm text-foreground-secondary capitalize">{level}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* License Type */}
            <div>
              <label className="text-sm font-semibold text-foreground-secondary mb-2 block">
                License Type
              </label>
              <div className="space-y-2">
                {licenseTypes.map(license => (
                  <label key={license} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.licenseTypes.includes(license)}
                      onChange={() => toggleFilter('licenseTypes', license)}
                      className="w-4 h-4 rounded border-border bg-background text-accent focus:ring-ring focus:ring-offset-0"
                    />
                    <span className="text-sm text-foreground-secondary capitalize">
                      {license.split('-').join(' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Popularity Trend */}
            <div>
              <label className="text-sm font-semibold text-foreground-secondary mb-2 block">
                Popularity Trend
              </label>
              <div className="space-y-2">
                {popularityTrends.map(trend => (
                  <label key={trend} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.popularityTrends.includes(trend)}
                      onChange={() => toggleFilter('popularityTrends', trend)}
                      className="w-4 h-4 rounded border-border bg-background text-accent focus:ring-ring focus:ring-offset-0"
                    />
                    <span className="text-sm text-foreground-secondary capitalize">
                      {trend.split('-').join(' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Community Size */}
            <div>
              <label className="text-sm font-semibold text-foreground-secondary mb-2 block">
                Community Size
              </label>
              <div className="space-y-2">
                {communitySizes.map(size => (
                  <label key={size} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.communitySizes.includes(size)}
                      onChange={() => toggleFilter('communitySizes', size)}
                      className="w-4 h-4 rounded border-border bg-background text-accent focus:ring-ring focus:ring-offset-0"
                    />
                    <span className="text-sm text-foreground-secondary capitalize">
                      {size.split('-').join(' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Compliance Requirements */}
          <div>
            <label className="text-sm font-semibold text-foreground-secondary mb-2 block">
              Compliance Requirements
            </label>
            <div className="flex flex-wrap gap-3">
              {complianceOptions.map(option => (
                <label key={option.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.complianceRequirements.includes(option.key)}
                    onChange={() => toggleFilter('complianceRequirements', option.key)}
                    className="w-4 h-4 rounded border-border bg-background text-accent focus:ring-ring focus:ring-offset-0"
                  />
                  <span className="text-sm text-foreground-secondary">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div>
            <label className="text-sm font-semibold text-foreground-secondary mb-2 block">
              Pricing Model
            </label>
            <div className="flex flex-wrap gap-2">
              {priceRanges.map(price => (
                <button
                  key={price}
                  onClick={() => toggleFilter('priceRanges', price)}
                  className={`px-3 py-1.5 rounded text-sm transition-colors ${
                    filters.priceRanges.includes(price)
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-background text-foreground-secondary hover:bg-background/80 border border-border'
                  }`}
                >
                  {price.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Apply filters to technology list
 */
export function applyFilters(
  technologies: TechnologyOption[],
  filters: VisualizationFilters
): TechnologyOption[] {
  return technologies.filter(tech => {
    // Category filter
    if (filters.categories.length > 0 && !filters.categories.includes(tech.category)) {
      return false;
    }

    // Maturity filter
    if (filters.maturityLevels.length > 0 && !filters.maturityLevels.includes(tech.technical.maturity)) {
      return false;
    }

    // License filter
    if (filters.licenseTypes.length > 0 && !filters.licenseTypes.includes(tech.license)) {
      return false;
    }

    // Compliance filter
    if (filters.complianceRequirements.length > 0) {
      const hasAllCompliance = filters.complianceRequirements.every(req => {
        return tech.compliance[req as keyof typeof tech.compliance] === true;
      });
      if (!hasAllCompliance) return false;
    }

    // Popularity trend filter
    if (filters.popularityTrends.length > 0 && !filters.popularityTrends.includes(tech.ecosystem.popularityTrend)) {
      return false;
    }

    // Community size filter
    if (filters.communitySizes.length > 0 && !filters.communitySizes.includes(tech.ecosystem.communitySize)) {
      return false;
    }

    // Price range filter
    if (filters.priceRanges.length > 0 && !filters.priceRanges.includes(tech.pricing.model)) {
      return false;
    }

    return true;
  });
}
